import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ClientData } from '../types';
import { BarChart3 } from 'lucide-react';
import './UsageChart.css';

interface UsageChartProps {
  clients: ClientData[];
  year: number;
  month: number;
}

const UsageChart: React.FC<UsageChartProps> = ({ clients, year, month }) => {
  // Preparar datos para el gráfico de barras (top 10 clientes)
  const topClients = clients
    .filter(client => client.hasData)
    .sort((a, b) => b.monthlyTotal - a.monthlyTotal)
    .slice(0, 10)
    .map(client => ({
      name: client.name.length > 15 ? client.name.substring(0, 15) + '...' : client.name,
      fullName: client.name,
      usage: client.monthlyTotal
    }));

  // Preparar datos para el gráfico de pie (distribución por rangos)
  const getRangeData = () => {
    const ranges = [
      { name: '0', min: 0, max: 0, count: 0, color: '#E5E7EB' },
      { name: '1-10', min: 1, max: 10, count: 0, color: '#FEE2E2' },
      { name: '11-50', min: 11, max: 50, count: 0, color: '#FBBF24' },
      { name: '51-100', min: 51, max: 100, count: 0, color: '#60A5FA' },
      { name: '100+', min: 101, max: Infinity, count: 0, color: '#34D399' }
    ];

    clients.forEach(client => {
      const usage = client.monthlyTotal;
      const range = ranges.find(r => usage >= r.min && usage <= r.max);
      if (range) range.count++;
    });

    return ranges.filter(range => range.count > 0);
  };

  const rangeData = getRangeData();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{payload[0].payload.fullName || label}</p>
          <p className="tooltip-value">
            Uso: <span>{payload[0].value.toLocaleString()}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">Rango: {payload[0].payload.name}</p>
          <p className="tooltip-value">
            Clientes: <span>{payload[0].value}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  if (topClients.length === 0) {
    return (
      <div className="usage-chart-container">
        <div className="chart-header">
          <BarChart3 size={24} />
          <h3>Gráficas de Uso</h3>
        </div>
        <div className="no-data-message">
          <p>No hay datos suficientes para mostrar gráficas</p>
        </div>
      </div>
    );
  }

  return (
    <div className="usage-chart-container">
      <div className="chart-header">
        <BarChart3 size={24} />
        <h3>Análisis de Uso - {year}/{month.toString().padStart(2, '0')}</h3>
      </div>

      <div className="charts-grid">
        {/* Gráfico de barras - Top 10 clientes */}
        <div className="chart-section">
          <h4>Top 10 Clientes por Uso</h4>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topClients} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="usage" 
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico de pie - Distribución por rangos */}
        <div className="chart-section">
          <h4>Distribución por Rangos de Uso</h4>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={rangeData}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={false}
                >
                  {rangeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Estadísticas rápidas */}
      <div className="quick-stats">
        <div className="stat-card">
          <span className="stat-number">{clients.filter(c => c.hasData).length}</span>
          <span className="stat-label">Clientes Activos</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{clients.reduce((sum, c) => sum + c.monthlyTotal, 0).toLocaleString()}</span>
          <span className="stat-label">Uso Total</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">
            {clients.filter(c => c.hasData).length > 0 
              ? Math.round(clients.reduce((sum, c) => sum + c.monthlyTotal, 0) / clients.filter(c => c.hasData).length).toLocaleString()
              : 0
            }
          </span>
          <span className="stat-label">Promedio por Cliente</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{Math.max(...clients.map(c => c.monthlyTotal)).toLocaleString()}</span>
          <span className="stat-label">Uso Máximo</span>
        </div>
      </div>
    </div>
  );
};

export default UsageChart;
